import { Outlet, useParams } from "react-router";
import useAppDialog from "@/features/ui/hooks/use-app-dialog";
import { useRecoilValue } from "recoil";
import { appToastsState } from "@/features/line-sheet-sets/recoils/app-toasts-state";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { useToast, UseToastOptions } from "@chakra-ui/react";
import { useEffect } from "react";
import AppDialog from "@/features/ui/app-dialog";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PwaInstallBanner from "@/shared/components/pwa-install-banner";
import AppVersionBanner from "@/shared/components/app-version-banner";
import useAnalytics from "@/shared/analytics/use-analytics";
import ChannelTalkLauncher from "@/shared/components/channel-talk-launcher";
import useChannelTalkBoot from "@/shared/hooks/useChannelTalkBoot";
import { getCanonicalUrl } from "@/shared/utils/canonical-url";
import { SITE_NAME } from "@/shared/constants/site";

export default function AppLayout() {
  // 여기가 모든 /:locale? 라우트(랜딩·패스픽 랜딩·auth·로그인 영역)가 통과하는 유일한 지점이다.
  // 404/500 은 이 트리 밖이라 라우트 변경 페이지뷰가 잡히지 않는다 (직접 진입은 GTM 구성 태그가 센다).
  useAnalytics();

  // 채널톡도 로그인 전 화면(랜딩·로그인·회원가입)에서 떠야 해서 여기서 boot 한다.
  useChannelTalkBoot();

  const appToasts = useRecoilValue(appToastsState);
  const { remove: removeAppToast } = useAppToasts();
  const toast = useToast();
  const { dialogs, closeDialog } = useAppDialog();

  const { locale: rawLocale } = useParams();
  const { i18n } = useTranslation();

  const location = useLocation();

  useEffect(() => {
    const locale = rawLocale || "en";

    if (locale !== i18n.language) {
      i18n.changeLanguage(locale);
    }
  }, [rawLocale, i18n]);

  useEffect(() => {
    if (appToasts.length > 0) {
      appToasts.forEach((appToast) => {
        const option: Omit<UseToastOptions, "id"> = {
          title: appToast.message,
          description: appToast.description,
          status: appToast.status,
          duration: 5000,
          position: "top",
          isClosable: true,
          // 메시지에 넣은 개행을 그대로 보여준다 — 개행이 없는 토스트에는 영향이 없다.
          containerStyle: { whiteSpace: "pre-line" },
          onCloseComplete: () => {
            removeAppToast(appToast.id);
          },
        };

        if (toast.isActive(appToast.id)) {
          toast.update(appToast.id, option);
        } else {
          toast({
            id: appToast.id,
            ...option,
          });
        }
      });
    }
  }, [toast, appToasts, removeAppToast]);

  useEffect(() => {
    toast.closeAll();
  }, [toast, location.pathname]);

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <link
          rel="canonical"
          href={getCanonicalUrl()}
        />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta
          property="og:url"
          content={getCanonicalUrl()}
        />
        <meta
          property="twitter:url"
          content={getCanonicalUrl()}
        />
      </Helmet>
      {dialogs.map((dialog) => {
        return <AppDialog key={dialog.id} {...dialog} />;
      })}
      <Outlet />{" "}
      {/* 라우터 안에 두는 이유: 언어는 위 useParams 이펙트로 확정되므로 _app.tsx에
          두면 배너가 en으로 먼저 그려졌다가 바뀐다. useLocation으로 제외 경로도 쓴다.
          404/500은 이 트리 밖이라 자연히 배너가 뜨지 않는다. */}
      <PwaInstallBanner />
      {/* 새 버전 감지 — 페이지를 옮길 때 새로고침하고, 한 화면에 머무는 동안에는 배너로 알린다. */}
      <AppVersionBanner />
      {/* 회사 타입과 관계없이 모두에게 띄운다. */}
      <ChannelTalkLauncher />
    </>
  );
}
